import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as logistaService from '@/services/logistaService';

function MetricCard({ icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-edge bg-surface p-5 shadow-sm">
      <div className="mb-2 text-2xl">{icon}</div>
      <p className="text-2xl font-black text-fg">{value ?? '—'}</p>
      <p className="mt-0.5 text-sm font-semibold text-muted">{label}</p>
      {sub && <p className="mt-1 text-xs text-muted">{sub}</p>}
    </div>
  );
}

function QuickLink({ to, icon, label, desc }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-4 rounded-2xl border border-edge bg-surface p-4 shadow-sm transition hover:border-accent/30 hover:shadow-md"
    >
      <span className="text-2xl">{icon}</span>
      <div>
        <p className="font-bold text-fg">{label}</p>
        <p className="text-xs text-muted">{desc}</p>
      </div>
      <span className="ml-auto text-muted">→</span>
    </Link>
  );
}

export default function LogistaDashboardPage() {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [meRes, analyticsRes] = await Promise.all([
          logistaService.getMe(),
          logistaService.getAnalytics().catch(() => ({ data: { data: null } })),
        ]);
        setData(meRes.data.data);
        setAnalytics(analyticsRes.data.data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent border-t-transparent" />
      </div>
    );
  }

  const sub = data?.subscription;
  const partner = data?.partner;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-black text-fg">
          Olá{partner?.name ? `, ${partner.name}` : ''}! 👋
        </h1>
        <p className="mt-1 text-sm text-muted">Bem-vindo ao teu painel de logista.</p>
      </div>

      {/* Trial / Subscription Banner */}
      {sub?.status === 'trial' && (
        <div className="flex items-center justify-between rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
          <div>
            <p className="font-bold text-amber-800">
              ⏳ Trial gratuito — {sub.trialDaysLeft} {sub.trialDaysLeft === 1 ? 'dia' : 'dias'} restantes
            </p>
            <p className="mt-0.5 text-sm text-amber-600">
              Ativa a subscrição para continuar a receber jogadores após o trial.
            </p>
          </div>
          <Link
            to="/logista/subscription"
            className="ml-4 shrink-0 rounded-xl bg-amber-600 px-4 py-2 text-sm font-bold text-white hover:bg-amber-700"
          >
            Ativar
          </Link>
        </div>
      )}

      {sub?.status === 'past_due' && (
        <div className="flex items-center justify-between rounded-2xl border border-red-200 bg-red-50 px-5 py-4">
          <div>
            <p className="font-bold text-red-800">⚠️ Pagamento em atraso</p>
            <p className="mt-0.5 text-sm text-red-600">Atualiza o método de pagamento para evitar interrupções.</p>
          </div>
          <Link
            to="/logista/subscription"
            className="ml-4 shrink-0 rounded-xl bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700"
          >
            Resolver
          </Link>
        </div>
      )}

      {sub?.status === 'active' && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-3">
          <p className="text-sm font-semibold text-emerald-700">
            ✅ Subscrição ativa
            {sub.currentPeriodEnd && (
              <span className="ml-2 font-normal text-emerald-600">
                — próxima cobrança em {new Date(sub.currentPeriodEnd).toLocaleDateString('pt-PT')}
              </span>
            )}
          </p>
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <MetricCard icon="🎫" label="Resgates este mês" value={analytics?.thisMonth} />
        <MetricCard icon="🎁" label="Total de resgates" value={analytics?.total} />
        <MetricCard icon="⭐" label="Oferta mais popular" value={analytics?.topOffer?.title || '—'} />
        <MetricCard
          icon="📊"
          label="Estado"
          value={sub?.status === 'trial' ? 'Trial' : sub?.status === 'active' ? 'Ativo' : '—'}
          sub={sub?.status === 'trial' ? `${sub.trialDaysLeft} dias restantes` : undefined}
        />
      </div>

      {/* Quick links */}
      <div>
        <h2 className="mb-3 font-serif text-lg font-bold text-fg">Acesso rápido</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <QuickLink to="/logista/loja" icon="🏪" label="A minha loja" desc="Edita informações, logo e foto de capa" />
          <QuickLink to="/logista/ofertas" icon="🎁" label="Ofertas" desc="Cria e gere as tuas promoções" />
          <QuickLink to="/logista/resgates" icon="🎫" label="Resgates" desc="Valida QR codes dos jogadores" />
          <QuickLink to="/logista/analytics" icon="📈" label="Analytics" desc="Vê o desempenho da tua loja" />
        </div>
      </div>
    </div>
  );
}
